import { SelectItem } from '@/types/ui';
import type { NestedKeySort } from '@/storybook/components/FormCollection/TreeSelect/TreeSelect.types';
import { useTreeSelectHandlers } from './useTreeSelectHandlers';
import { useTreeSelectOptions } from './useTreeSelectOptions';
import { useTreeSelectRendering } from './useTreeSelectRendering';
import { useTreeSelectVirtualization } from './useTreeSelectVirtualization';

type UseTreeSelectParams<T extends string> = {
  options: SelectItem<T>[];
  value: T[];
  onChange: (value: T[]) => void;
  collapseSingleChild?: boolean;
  selectionMode: 'standard' | 'indication';
  nestedKeySort: NestedKeySort;
  hideCheckboxes?: boolean;
  disabled?: boolean;
  treeListItemClassName?: string;
  optionLabels?: ReadonlyMap<string, string>;
};

export function useTreeSelect<T extends string>({
  options,
  value,
  onChange,
  collapseSingleChild,
  selectionMode,
  nestedKeySort,
  hideCheckboxes,
  disabled,
  treeListItemClassName,
  optionLabels,
}: UseTreeSelectParams<T>) {
  const { fixedOptions, nestedOptions, descendantMap, disabledPathSet, sortedNestedOptions, valueLabelList } =
    useTreeSelectOptions<T>({
      options,
      value,
      collapseSingleChild,
      selectionMode,
      nestedKeySort,
      optionLabels,
    });

  const { searchInput, setSearchInput, searchResults, branchOverrides, handleClickOption, handleToggleBranch } =
    useTreeSelectHandlers<T>({
      value,
      onChange,
      nestedOptions,
      descendantMap,
      selectionMode,
      hideCheckboxes,
      optionLabels,
    });

  const { visibleRows, renderTreeRow } = useTreeSelectRendering<T>({
    nestedOptions: sortedNestedOptions,
    value,
    searchInput,
    searchResults,
    branchOverrides,
    selectionMode,
    disabled,
    disabledPathSet,
    hideCheckboxes,
    treeListItemClassName,
    optionLabels,
    handleClickOption,
    handleToggleBranch,
  });

  const virtualization = useTreeSelectVirtualization(visibleRows);

  return {
    fixedOptions,
    disabledPathSet,
    valueLabelList,
    searchInput,
    setSearchInput,
    searchResults,
    visibleRows,
    renderTreeRow,
    virtualization,
  };
}
